/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import api from '../../services/api';
import Toast from '../common/Toast';
import { MapWithFlexContainer } from '../common/Map';
import {
  locationCell, useWindowDimensions,
} from '../../services/helpers';
import CustomTable from '../common/Table';

const DriverLocationTrack = ({
  data, googleMapsKey, routeId, timezoneFromServer, route,
}) => {
  // eslint-disable-next-line no-unused-vars
  const { height, width } = useWindowDimensions();
  const [driverTrackingData, setDriverTrackingData] = useState(data);
  // eslint-disable-next-line no-unused-vars
  const [renderMap, setRenderMap] = useState(true);
  const [markers, setMarkers] = useState([]);
  const [serverTimezone, setServerTimezone] = useState('America/Chicago');
  const [lines, setLines] = useState([]);
  const [routeData, setRouteData] = useState({});
  const [tableData, setTableData] = useState([]);
  useEffect(() => setRouteData(JSON.parse(route)), [route]);
  useEffect(() => {
    const timer = setInterval(() => {
      api.routesDashboard.track(routeId).then((res) => {
        if (res.ok) {
          setDriverTrackingData(res.data);
        }
      });
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const tz = timezoneFromServer?.info?.identifier;
    if (tz) {
      setServerTimezone(tz);
    } else {
      setServerTimezone('America/Chicago');
    }
  }, [timezoneFromServer]);

  useEffect(() => {
    if (driverTrackingData.length > 0) {
      const points = [...driverTrackingData].filter(p => Boolean(p.geolocation) && Boolean(p.geolocation.latitude) && Boolean(p.geolocation.longitude));

      const driverTableRows = points.map((point) => {
        const dateTime = point.timestamp ? moment(point.timestamp).tz(point.zone ?? serverTimezone) : null;
        const sortTime = dateTime ? dateTime.valueOf() : 0;

        return {
          id: `driver-${point.id}`,
          timestamp: dateTime,
          type: 'Driver Location',
          latitude: point.geolocation.latitude,
          longitude: point.geolocation.longitude,
          sortTime,
          hasTime: Boolean(point.time),
          isDriverTracking: true,
        };
      });

      const pointIdToTimestamp = driverTableRows.reduce((acc, row) => {
        const idStr = String(row.id);
        const rawId = idStr.startsWith('driver-') ? idStr.replace('driver-', '') : idStr;
        acc[rawId] = row.timestamp;
        return acc;
      }, {});

      const pointsForMarkers = [...points];
      const firstPoint = pointsForMarkers.shift();
      const lastPoint = pointsForMarkers.length ? pointsForMarkers.pop() : null;

      const getMarkerTip = (point) => {
        if (!point) return '';
        const ts = pointIdToTimestamp[point.id];
        if (!ts) return '';
        return ts.format('hh:mm A zz');
      };

      const processedMarkers = [
        {
          position: { lat: firstPoint?.geolocation?.latitude, lng: firstPoint?.geolocation?.longitude },
          tip: getMarkerTip(firstPoint),
          color: 'green',
        },
        ...pointsForMarkers.map(point => ({
          position: { lat: point?.geolocation?.latitude, lng: point?.geolocation?.longitude },
          tip: getMarkerTip(point),
          color: 'blue',
        })),
      ];
      if (lastPoint) {
        processedMarkers.push({
          position: { lat: lastPoint?.geolocation?.latitude, lng: lastPoint?.geolocation?.longitude },
          tip: getMarkerTip(lastPoint),
          color: 'red',
        });
      }

      const routes = [];
      for (let i = 0; i < processedMarkers.length - 1; i += 1) {
        const routeSegment = [];
        routeSegment.push({ coords: `${processedMarkers[i].position.lat},${processedMarkers[i].position.lng}` });
        routeSegment.push({ coords: `${processedMarkers[i + 1].position.lat},${processedMarkers[i + 1].position.lng}` });
        routes.push(routeSegment);
      }
      setLines(routes);

      for (let i = 0; i < routeData.route_manifest_stops?.length || 0; i += 1) {
        if (Boolean(routeData.route_manifest_stops[i].pickup?.geolocation?.latitude) && Boolean(routeData.route_manifest_stops[i].pickup?.geolocation?.longitude)) {
          processedMarkers.push({
            position: { lat: routeData.route_manifest_stops[i].pickup?.geolocation?.latitude, lng: routeData.route_manifest_stops[i].pickup?.geolocation?.longitude },
            tip: 'Route Stop',
            color: 'yellow',
          });
        }
      }
      setMarkers(processedMarkers);

      const routeStopRows = [];
      if (routeData.route_manifest_stops) {
        routeData.route_manifest_stops.forEach((stop) => {
          if (stop.pickup?.geolocation?.latitude && stop.pickup?.geolocation?.longitude) {
            const firstPhoto = stop.pickup.first_photo;
            const hasTime = Boolean(firstPhoto?.timestamp);
            const dateTime = hasTime ? moment(firstPhoto.timestamp).tz(firstPhoto.zone ?? serverTimezone) : null;
            const sortTime = dateTime ? dateTime.valueOf() : (stop.position || 0);

            routeStopRows.push({
              id: `route-stop-${stop.id}`,
              timestamp: dateTime,
              type: 'Scan/Picture',
              latitude: stop.pickup.geolocation.latitude,
              longitude: stop.pickup.geolocation.longitude,
              sortTime,
              hasTime,
              isDriverTracking: false,
            });
          }
        });
      }

      const allTableRows = [...driverTableRows, ...routeStopRows].sort((a, b) => {
        // First sort by hasTime (points with time first), then by sortTime
        if (a.hasTime !== b.hasTime) {
          return b.hasTime ? 1 : -1;
        }
        return a.sortTime - b.sortTime;
      });

      const driverTrackingIndices = [];
      allTableRows.forEach((row, index) => {
        if (row.isDriverTracking) {
          driverTrackingIndices.push(index);
        }
      });

      if (driverTrackingIndices.length > 0) {
        const firstDriverIndex = driverTrackingIndices[0];
        const lastDriverIndex = driverTrackingIndices[driverTrackingIndices.length - 1];

        if (firstDriverIndex === lastDriverIndex) {
          allTableRows[firstDriverIndex].type = 'Start';
        } else {
          allTableRows[firstDriverIndex].type = 'Start';
          allTableRows[lastDriverIndex].type = 'Finish';
        }
      }

      const finalTableRows = allTableRows.map((row) => {
        const {
          isDriverTracking, sortTime, hasTime, ...rest
        } = row;
        return rest;
      });

      setTableData(finalTableRows);
    } else {
      setMarkers([]);
      setTableData([]);
      setLines([]);
    }
  }, [driverTrackingData, routeData, serverTimezone]);

  return (
    <>
      <Toast />
      <div className="row">
        <div className="col-xs-12">
          <div className="box">
            <div className="box-header">
              <h1 className="box-title">Driver Location Track</h1>
            </div>
            <ul>
              <li>
                Driver:
                {routeData.driver?.user?.first_name}
                {' '}
                {routeData.driver?.user?.last_name}
              </li>
              <li>
                Mileage:
                {routeData.mileage}
              </li>
              <li>
                Cost:
                {routeData.estimated_cost}
              </li>
            </ul>
            <div className="box-body main_listing">
              <div style={{ height: height * 0.7 }}>
                { renderMap && (
                  <MapWithFlexContainer
                    markers={markers}
                    smallMarkers
                    routes={lines}
                    googleMapURL={`https://maps.googleapis.com/maps/api/js?key=${googleMapsKey}&v=3.exp&libraries=geometry,drawing,places`}
                  />
                )}
              </div>
            </div>
            {tableData.length > 0 && (
              <div className="box-body" style={{ marginTop: '20px' }}>
                <CustomTable
                  rows={tableData}
                  rowsProps={[
                    { header: 'Number of Track', key: '#' },
                    { header: 'Timestamp', key: 'timestamp', format: true },
                    { header: 'Type', key: 'type' },
                    { header: 'Location', key: 'location', format: true },
                  ]}
                  formatters={{
                    timestamp: row => (row?.timestamp?.isValid?.() ? row.timestamp.format('MM-DD-YYYY - hh:mm A zz') : '-'),
                    location: row => locationCell(row),
                  }}
                  suffix="driver-location-track-table"
                  blockRowPePage
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

DriverLocationTrack.propTypes = {
  data: PropTypes.array.isRequired,
  googleMapsKey: PropTypes.string.isRequired,
  route: PropTypes.any.isRequired,
  timezoneFromServer: PropTypes.any.isRequired,
  routeId: PropTypes.any.isRequired,
};

export default DriverLocationTrack;
